import { useState, useEffect, useRef } from "preact/hooks";
import { PaymentGateway, PaymentNotification, PaymentStatus, ProcessorRef, TransactionStatusPoller } from "../../../../../../services/payment-gateway";
import Config from "../../../../config";
import { MarketingEvents } from "../../../../../../utils/gtm-module";

export type Steps = 'start' | 'wait' | 'error' | 'external-pay-form' | 'pending' | 'success-wait' | 'success';

export interface UseDepositTransactionArgs {
    provider: ProcessorRef;
}

export const useDepositTransaction = ({ provider }: UseDepositTransactionArgs) => {
    const service = useRef(new PaymentGateway(Config.softionAPI));
    const poller = useRef<TransactionStatusPoller | null>(null);

    const [step, setStep] = useState<Steps>('start');
    const [error, setError] = useState<string | null>(null);
    const [formUrl, setFormUrl] = useState<string | null>(null);
    const [statusUrl, setStatusUrl] = useState<string | null>(null);

    const reset = () => {
        if (step == "success" || step == "error" || step == "success-wait")
            setStep("start");
    }

    useEffect(() => {
        return () => {
            poller.current?.stop();
        };
    }, []);

    const handleTransactionStatus = (status: PaymentNotification) => {
        if (status.status === PaymentStatus.SUCCESS) {
            window.dispatchEvent(new CustomEvent(MarketingEvents.DEPOSIT_SUCCESS, { detail: { amount: status.amount } }));
            setStep('success');
        } else if (status.status === PaymentStatus.FAILED) {
            setError(status.error.source + ": " + status.error.msg);
            setStep('error');
        }
    };
    
    useEffect(() => {
        if (step === 'pending' && statusUrl) {
            poller.current = new TransactionStatusPoller(statusUrl);
            poller.current.addListener(handleTransactionStatus);
            poller.current.start();
        } else {
            poller.current?.stop();
        }
    }, [step, statusUrl]);


    const initiateTransaction = async (amount: string) => {
        setStep('wait');
        setError(null);

        try {
            const response = await service.current.requestDeposit(provider.provider, provider.processor, amount);

            if (response.status === 'error') {
                setError(response.error.source + ": " + response.error.msg);
                setStep('error');
                return;
            }

            if (response.data.redirecturl) {
                setFormUrl(response.data.redirecturl);
                setStep('external-pay-form');
            } else if (response.data.statusUrl) {
                setStatusUrl(response.data.statusUrl);
                setStep('pending');
            } else {
                setStep('success-wait');
            }
        } catch (e) {
            setError("An unexpected error occurred.");
            setStep('error');
        }
    };

    return {
        step,
        error,
        formUrl,
        statusUrl,
        initiateTransaction,
        handleTransactionStatus,
        reset
    };
};